import { useState, useEffect, useRef, useCallback } from 'react';
import { VoiceService } from '../services/voice-service';
import type { 
  VoiceRecordingState, 
  UseVoiceRecordingReturn, 
  AudioTranscriptionResponse,
  VoiceMessageConfig,
  VoiceError
} from '../types/voice';

export const useVoiceRecording = (config?: Partial<VoiceMessageConfig>): UseVoiceRecordingReturn => {
  const [recordingState, setRecordingState] = useState<VoiceRecordingState>({
    isRecording: false,
    isProcessing: false,
    recordingDuration: 0,
    audioLevel: 0,
    error: null,
    hasPermission: false,
    isSupported: VoiceService.isSupported()
  });

  const voiceServiceRef = useRef<VoiceService | null>(null);
  const recordingTimerRef = useRef<number | null>(null);
  const maxDurationTimerRef = useRef<number | null>(null);

  // Initialize voice service
  useEffect(() => {
    if (recordingState.isSupported) {
      voiceServiceRef.current = new VoiceService(config);
    }

    return () => {
      cleanup();
    };
  }, [config]);

  // Check initial microphone permission
  useEffect(() => {
    const checkPermission = async () => {
      if (!voiceServiceRef.current) return;

      try {
        const permission = await voiceServiceRef.current.checkMicrophonePermission();
        setRecordingState(prev => ({
          ...prev,
          hasPermission: permission === 'granted'
        }));
      } catch (error) {
        console.warn('Could not check microphone permission:', error);
      }
    };

    checkPermission();
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    if (maxDurationTimerRef.current) {
      clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }

    if (voiceServiceRef.current) {
      voiceServiceRef.current.cleanup();
    }
  }, []);

  // Start recording timer
  const startRecordingTimer = useCallback(() => {
    recordingTimerRef.current = setInterval(() => {
      setRecordingState(prev => ({
        ...prev,
        recordingDuration: prev.recordingDuration + 1
      }));
    }, 1000);
  }, []);

  // Stop recording timer
  const stopRecordingTimer = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  // Handle audio level updates
  const handleAudioLevel = useCallback((level: number) => {
    setRecordingState(prev => ({
      ...prev,
      audioLevel: level
    }));
  }, []);

  // Handle voice errors
  const handleVoiceError = useCallback((error: VoiceError | Error) => {
    const errorMessage = error instanceof Error ? error.message : error.message;
    
    setRecordingState(prev => ({
      ...prev,
      error: errorMessage,
      isRecording: false,
      isProcessing: false
    }));

    cleanup();
  }, [cleanup]);

  // Check microphone permission
  const checkMicrophonePermission = useCallback(async (): Promise<boolean> => {
    if (!voiceServiceRef.current) {
      return false;
    }

    try {
      const permission = await voiceServiceRef.current.checkMicrophonePermission();
      const hasPermission = permission === 'granted';
      
      setRecordingState(prev => ({
        ...prev,
        hasPermission
      }));

      return hasPermission;
    } catch (error) {
      handleVoiceError(error as VoiceError);
      return false;
    }
  }, [handleVoiceError]);

  // Request microphone access
  const requestMicrophoneAccess = useCallback(async (): Promise<void> => {
    if (!voiceServiceRef.current) {
      throw new Error('Voice service not initialized');
    }

    try {
      await voiceServiceRef.current.requestMicrophoneAccess();
      
      setRecordingState(prev => ({
        ...prev,
        hasPermission: true,
        error: null
      }));
    } catch (error) {
      setRecordingState(prev => ({
        ...prev,
        hasPermission: false
      }));
      throw error;
    }
  }, []);

  // Start recording
  const startRecording = useCallback(async (): Promise<void> => {
    if (!voiceServiceRef.current || !recordingState.isSupported) {
      handleVoiceError(new Error('Voice recording not supported'));
      return;
    }

    try {
      setRecordingState(prev => ({
        ...prev,
        isRecording: false,
        isProcessing: true,
        error: null,
        recordingDuration: 0,
        audioLevel: 0
      }));

      // Check if we need to request microphone access
      if (!recordingState.hasPermission || !voiceServiceRef.current.isAudioStreamActive()) {
        console.log('Requesting microphone access for recording...');
        await voiceServiceRef.current.requestMicrophoneAccess();
      } else {
        console.log('Microphone access already granted, proceeding with recording...');
      }

      // Start audio level monitoring
      voiceServiceRef.current.startAudioLevelMonitoring(handleAudioLevel);

      // Start recording
      await voiceServiceRef.current.startRecording();

      setRecordingState(prev => ({
        ...prev,
        isRecording: true,
        isProcessing: false,
        hasPermission: true
      }));

      // Start recording timer
      startRecordingTimer();

      // Set maximum duration timer
      const maxDuration = config?.maxRecordingDuration || 60;
      maxDurationTimerRef.current = setTimeout(() => {
        stopRecording();
      }, maxDuration * 1000);

    } catch (error) {
      handleVoiceError(error as VoiceError);
    }
  }, [recordingState.isSupported, config?.maxRecordingDuration, handleVoiceError, handleAudioLevel, startRecordingTimer]);

  // Stop recording
  const stopRecording = useCallback((): void => {
    console.log('Stopping recording...');
    if (!voiceServiceRef.current || !recordingState.isRecording) {
      return;
    }

    try {
      stopRecordingTimer();
      
      if (maxDurationTimerRef.current) {
        clearTimeout(maxDurationTimerRef.current);
        maxDurationTimerRef.current = null;
      }

      voiceServiceRef.current.stopAudioLevelMonitoring();

      // Don't set isRecording to false here - let getRecordingBlob handle it
      // This ensures the state change happens after we get the blob
      setRecordingState(prev => ({
        ...prev,
        isRecording: false,
        audioLevel: 0
      }));

    } catch (error) {
      handleVoiceError(error as VoiceError);
    }
    console.log('Recording stopped successfully');
  }, [recordingState.isRecording, stopRecordingTimer, handleVoiceError]);

  // Cancel recording
  const cancelRecording = useCallback((): void => {
    if (!voiceServiceRef.current) {
      return;
    }

    try {
      stopRecordingTimer();
      
      if (maxDurationTimerRef.current) {
        clearTimeout(maxDurationTimerRef.current);
        maxDurationTimerRef.current = null;
      }

      voiceServiceRef.current.cancelRecording();

      setRecordingState(prev => ({
        ...prev,
        isRecording: false,
        isProcessing: false,
        recordingDuration: 0,
        audioLevel: 0,
        error: null
      }));

    } catch (error) {
      handleVoiceError(error as VoiceError);
    }
  }, [stopRecordingTimer, handleVoiceError]);

  // Transcribe audio
  const transcribeAudio = useCallback(async (audioBlob: Blob): Promise<AudioTranscriptionResponse> => {
    if (!voiceServiceRef.current) {
      throw new Error('Voice service not available');
    }

    try {
      setRecordingState(prev => ({
        ...prev,
        isProcessing: true,
        error: null
      }));

      const response = await voiceServiceRef.current.transcribeAudio({
        audio: audioBlob,
        language: 'vi' // Default to Vietnamese
      });

      setRecordingState(prev => ({
        ...prev,
        isProcessing: false
      }));

      return response;

    } catch (error) {
      setRecordingState(prev => ({
        ...prev,
        isProcessing: false
      }));
      
      handleVoiceError(error as VoiceError);
      throw error;
    }
  }, [handleVoiceError]);

  // Get audio blob from current recording
  const getRecordingBlob = useCallback(async (): Promise<Blob | null> => {
    if (!voiceServiceRef.current) {
      return null;
    }

    // Check if there's actually an active recording before trying to stop it
    // Note: We check the MediaRecorder state directly since our state might already be updated
    try {
      const blob = await voiceServiceRef.current.stopRecording();
      
      // Update state to reflect that recording has stopped
      setRecordingState(prev => ({
        ...prev,
        isRecording: false,
        audioLevel: 0
      }));
      
      return blob;
    } catch (error) {
      // If stopRecording fails (e.g., no active recording), that's expected in some cases
      console.warn('getRecordingBlob: No active recording to stop');
      return null;
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setRecordingState(prev => ({
      ...prev,
      error: null
    }));
  }, []);

  // Reset recording state
  const resetRecording = useCallback(() => {
    cancelRecording();
    setRecordingState(prev => ({
      ...prev,
      recordingDuration: 0,
      audioLevel: 0,
      error: null
    }));
  }, [cancelRecording]);

  return {
    recordingState,
    startRecording,
    stopRecording,
    cancelRecording,
    transcribeAudio,
    checkMicrophonePermission,
    requestMicrophoneAccess,
    isSupported: recordingState.isSupported,
    getRecordingBlob,
    clearError,
    resetRecording
  };
};